import { inject, computed, type ComputedRef, type Ref } from "vue";
import type { FeatureFlag } from "../types";
import { FEATURE_FLAG_SERVICE_KEY } from "../utils/symbols";

interface UseFeatureFlagsReturn {
  flags: ComputedRef<Record<string, FeatureFlag>>;
  isLoading: ComputedRef<boolean>;
  error: ComputedRef<Error | null>;
  isFeatureEnabled: (flagName: string) => boolean;
}

/**
 * Composable to access all feature flags and related data.
 *
 * @returns {UseFeatureFlagsReturn} An object containing:
 *   - flags: Computed ref with all feature flags
 *   - isLoading: Computed ref with loading state
 *   - error: Computed ref with any error that occurred
 *   - isFeatureEnabled: Function to check if a specific flag is enabled
 *
 * @throws {Error} If used outside of FeatureFlagProvider
 *
 * @example
 * // Basic usage
 * <script setup>
 * import { useFeatureFlags } from '@flagpole/client-vue';
 *
 * const { flags, isLoading, error, isFeatureEnabled } = useFeatureFlags();
 * </script>
 *
 * <template>
 *   <div v-if="isLoading">Loading...</div>
 *   <div v-else-if="error">Error: {{ error.message }}</div>
 *   <div v-else>
 *     <div v-for="(flag, name) in flags" :key="name">
 *       {{ name }}: {{ flag.isEnabled ? 'On' : 'Off' }}
 *     </div>
 *   </div>
 * </template>
 */
export function useFeatureFlags(): UseFeatureFlagsReturn {
  const service = inject(FEATURE_FLAG_SERVICE_KEY);

  if (!service) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagProvider"
    );
  }

  const flags = computed(() => service.flags.value);
  const isLoading = computed(() => service.isLoading.value);
  const error = computed(() => service.error.value);

  const isFeatureEnabled = (flagName: string): boolean => {
    return service.isFeatureEnabled(flagName);
  };

  return {
    flags,
    isLoading,
    error,
    isFeatureEnabled,
  };
}
